import { create } from 'zustand'
import { format, parse, parseISO } from 'date-fns'
import type { Aluno, Captador, Curso, ValoresParceiros, MatriculaResult } from '@/types'
import * as cursosApi from '@/api/cursos'
import * as matriculasApi from '@/api/matriculas'
import * as alunosApi from '@/api/alunos'
import * as parceirosApi from '@/api/parceiros'
import { COURSE_CATEGORIES } from '@/lib/constants'
import { getApiErrorMessage } from '@/lib/apiError'

function formatIsoToBrazilian(isoDate: string | undefined | null): string {
  if (!isoDate) return ''
  const withoutTimezone = isoDate.replace(/Z$/, '')
  return format(parseISO(withoutTimezone), 'dd/MM/yyyy')
}

function parseBrazilianToIso(brazilianDate: string): string {
  const parsed = parse(brazilianDate, 'dd/MM/yyyy', new Date())
  return format(parsed, 'yyyy-MM-dd')
}

export interface MatriculaFormValues {
  cpf: string
  nome: string
  dataNascimento: string
  telefone: string
  email: string
  cnh: string
  categoriaCnh: string
  dataPrimeiraHabilitacao: string
  renach: string
  rg: string
  municipio: string
  ufDestino: string
}

export interface TreeNode {
  title: string
  value: string
  selectable: boolean
  children: Array<{ title: string; value: number; selectable: boolean }>
}

interface ValidacaoAluno {
  loading: boolean
  executou: boolean
  valido: boolean
  tipoErro: string
  mensagemErro: string
}

// The floor the parceiro owes for the whole selection: the sum of every chosen course's cost.
// Single-course selections sum a single entry, so there is one rule, not two.
function pisoTotal(state: {
  cursoSelecionado: number | null
  cursosAdicionais: (number | null)[]
  valoresPorCurso: Record<number, ValoresParceiros>
}): number {
  const ids = [state.cursoSelecionado, ...state.cursosAdicionais].filter(
    (id): id is number => id != null
  )
  return ids.reduce((soma, id) => soma + (state.valoresPorCurso[id]?.valorNewDriver ?? 0), 0)
}

interface MatriculaState {
  alunoExistente: Aluno | null
  searchingCpf: boolean
  cnhFile: File | null
  cnhExtraindo: boolean
  cnhExtraida: parceirosApi.CamposDaCnh | null // null = nothing uploaded yet; {} = read yielded nothing
  allCursos: Curso[]
  cursosTree: TreeNode[]
  cursoSelecionado: number | null
  cursosAdicionais: (number | null)[] // extra courses to enroll the same student in; null = row not yet chosen
  ufDestino: string
  campos: string[]
  captadores: Captador[]
  captadorId: number | null
  contaDeRecebimento: parceirosApi.ContaDeRecebimentoDaVenda | null
  valoresParceiros: ValoresParceiros | null
  valoresPorCurso: Record<number, ValoresParceiros> // every selected course's values, for the multi-course total
  semPrecoPorCurso: Record<number, boolean>
  loadingValores: boolean
  valor: number
  valorEditadoManualmente: boolean // once the parceiro types a price, the floor stops overwriting it
  modalidadeCobranca: 'link' | 'manual'
  validacaoAluno: ValidacaoAluno
  validacaoPorCurso: Record<number, ValidacaoAluno> // each course validates on its own (state rules differ)
  validacaoSnapshot: string | null // chaveDaValidacao dos dados que produziram o veredito em tela
  matriculaResult: MatriculaResult | null
  cobrancaAgregada: matriculasApi.CobrancaAgregadaResult | null
  loading: boolean

  setCnhFile: (file: File | null) => void
  extrairDadosDaCnh: (file: File) => Promise<void>
  addCursoAdicional: () => void
  setCursoAdicional: (index: number, cursoId: number | null) => void
  removeCursoAdicional: (index: number) => void
  criarMatriculasComCobranca: (
    cnpjParceiro: string,
    ufParceiro: string,
    values: MatriculaFormValues
  ) => Promise<void>
  clearAluno: () => void
  setUfDestino: (uf: string) => void
  searchAlunoByCpf: (
    cpf: string,
    setValues: (values: Partial<MatriculaFormValues>) => void
  ) => Promise<void>
  loadCursos: (ufDestino: string) => Promise<void>
  recalcularParaUf: (cnpjParceiro: string, ufParceiro: string, ufDestino: string) => Promise<void>
  setCurso: (cursoId: number | null) => void
  getValoresParceiros: (
    cnpjParceiro: string,
    ufParceiro: string,
    ufDestino: string,
    cursoId: number
  ) => Promise<void>
  getValoresCursoAdicional: (
    cnpjParceiro: string,
    ufParceiro: string,
    ufDestino: string,
    cursoId: number
  ) => Promise<void>
  getCamposFormulario: (ufDestino: string, cursoId: number) => Promise<void>
  loadCaptadores: () => Promise<void>
  setCaptador: (captadorId: number | null) => void
  verificarContaDeRecebimento: () => Promise<void>
  setValor: (valor: number) => void
  sincronizarValorComPiso: () => void
  setModalidadeCobranca: (mode: 'link' | 'manual') => void
  validarAluno: (data: matriculasApi.ValidarAlunoData) => Promise<void>
  validarTodosOsCursos: (values: MatriculaFormValues) => Promise<boolean>
  validarSelecao: (values: MatriculaFormValues) => Promise<boolean>
  limparValidacao: () => void
  criarMatricula: (
    cnpjParceiro: string,
    ufParceiro: string,
    values: MatriculaFormValues
  ) => Promise<void>
  resetForm: () => void
}

/**
 * "Sem preço" chega de duas formas: um curso que o parceiro não precificou vem 200 com
 * valorNewDriver nulo (AC-15), e um captador sem markup vem 400 (AC-14). As duas viram null aqui,
 * senão o 400 escapa e a tela segue exibindo os valores do vendedor anterior.
 */
async function buscarValoresOuNulo(params: {
  cnpjParceiro: string
  ufParceiro: string
  ufDestino: string
  cursoId: number
}): Promise<ValoresParceiros | null> {
  try {
    const valores = await matriculasApi.getValoresParceiros({
      curso: params.cursoId,
      cnpjParceiro: params.cnpjParceiro,
      ufParceiro: params.ufParceiro,
      ufDestino: params.ufDestino,
    })
    return valores.valorNewDriver == null ? null : valores
  } catch {
    return null
  }
}

// Cada estado exige um conjunto próprio de dados para validar — PE se resolve com UF, curso e
// nascimento, GO ainda quer município, CNH e categoria — e quem sabe disso é o flow que monta os
// campos da tela. Estes três são o avesso disso: existem para matricular e nada dizem ao DETRAN.
export const CAMPOS_DISPENSADOS_NA_VALIDACAO: string[] = ['nome', 'telefone', 'email']

// Um veredito de validação fala sobre os dados que a consulta usou para identificar o aluno e o
// curso — mudou um deles, ele não descreve mais o que está na tela. Preencher os dados de contato
// depois de validar, ao contrário, não pode apagar uma resposta que continua valendo.
export function chaveDaValidacao(
  state: Pick<MatriculaState, 'cursoSelecionado' | 'cursosAdicionais'>,
  values: MatriculaFormValues
): string {
  const dadosDaConsulta = Object.entries(values).filter(
    ([campo]) => !CAMPOS_DISPENSADOS_NA_VALIDACAO.includes(campo)
  )
  return JSON.stringify([dadosDaConsulta, state.cursoSelecionado, state.cursosAdicionais])
}

function vereditoDaValidacao(state: MatriculaState): boolean {
  if (!state.cursosAdicionais.some((id) => id != null)) return state.validacaoAluno.valido
  const ids = [state.cursoSelecionado, ...state.cursosAdicionais].filter(
    (id): id is number => id != null
  )
  return ids.every((id) => state.validacaoPorCurso[id]?.valido)
}

const emptyValidacao: ValidacaoAluno = {
  loading: false,
  executou: false,
  valido: false,
  tipoErro: '',
  mensagemErro: '',
}

export const useMatriculaStore = create<MatriculaState>((set, get) => ({
  alunoExistente: null,
  searchingCpf: false,
  cnhFile: null,
  cnhExtraindo: false,
  cnhExtraida: null,
  allCursos: [],
  cursosTree: [],
  cursoSelecionado: null,
  cursosAdicionais: [],
  ufDestino: '',
  campos: [],
  captadores: [],
  captadorId: null,
  contaDeRecebimento: null,
  valoresParceiros: null,
  valoresPorCurso: {},
  semPrecoPorCurso: {},
  loadingValores: false,
  valor: 0,
  valorEditadoManualmente: false,
  modalidadeCobranca: 'link',
  validacaoAluno: emptyValidacao,
  validacaoPorCurso: {},
  validacaoSnapshot: null,
  // matriculaResult: { id: 1959, uf: 'GO', renach: '', statusDetran: 'MATRICULADO', cpfInstrutor: null, dataInicioCurso: null, dataFimCurso: null, dataInicioModuloTeorico: null, dataFimModuloTeorico: null, dataInicioModuloPratico: null, dataFimModuloPratico: null, dataValidadeCurso: null, identificacaoCertificado: null, identificacaoCertificadoRetornado: null, identificacaoCertificadoRetornadoTeorico: null, nota: null, notaPratica: null, dataValidacaoFacialInicial: '2025-04-11T18:26:36.060Z', dataValidacaoFacialFinal: null, ticketDetranRS: null, sendTrack: '2024-07-25T13:33:01.698Z', categoriaCnh: '', permiteEmissaoCertificado: true, dataEmissaoCertificado: null, parceiroCursoPraticoId: null, dataMatriculaDetran: null, ufMatriculaDetran: null, identificacaoDeclaracao: 'c92d35a5-4376-4b51-9595-7e7e1dfc1a24', alunoId: 2, cursoId: 1, parceiroId: null, captadorId: null, turmaId: null, pagamentoId: 1681, createdAt: '2024-12-13T12:41:06.934Z', updatedAt: '2026-03-13T22:11:18.265Z', deletedAt: null, aluno: { id: 2, cpf: '030.953.881-52', rg: '', cnh: '123123123-12', categoriaCnh: '', dataPrimeiraHabilitacao: null, estado: 'PE', municipio: '', azurePersonId: '53b6d467-bc30-4f2a-96b1-b2b595eb73c7', dataNascimento: '1989-05-05T03:00:00.000Z', email: 'victorivens05@gmail.com', nome: 'VICTOR IVENS PEREIRA', telefone: '(62) 99991-1222', avatar: null, cor: 'rgb(126, 27, 25)', userDeletionId: null, ignorarValidacaoSerpro: true, ignorarValidacaoAzure: null, rdLeadId: null, rdContactId: null, betaTester: null, createdAt: '2020-09-28T18:58:19.355Z', updatedAt: '2026-04-02T18:06:08.511Z', deletedAt: null, }, pagamento: { id: 1681, hashPagamento: 'lmlA4yCd', dataPagamento: null, dataVencimento: '2024-12-20T03:00:00.000Z', referenciaPagamento: '2252359', comboVsoft: null, valorVsoft: null, provedorPagamento: null, meioPagamento: null, valor: 0, valorOriginal: null, valorNewDriver: null, infoPix: null, infoBoleto: null, infoCartao: null, splits: [], descricaoConfirmacao: null, usuarioId: 2, cupomId: null, notaFiscalId: null, confirmadoPorUsuarioId: null, createdAt: '2026-03-13T22:11:18.120Z', updatedAt: '2026-03-13T22:11:18.120Z', deletedAt: null, cupom: null, }, curso: { id: 1, nome: 'Reciclagem para Condutores Infratores', cargaHoraria: 30, limiteHorasDia: 8, duracaoMaximaDias: 45, necessitaValidacaoFacial: true, diasParaEmissaoCertificado: 4, tipoCurso: 'CREDENCIADO_DETRAN', nomeReduzido: 'Reciclagem', sigla: null, urlSlug: 'reciclagem-para-condutores-infratores', categorias: 'Curso de Reciclagem', validadeCurso: 5, createdAt: '2020-02-26T05:42:44.971Z', updatedAt: '2026-03-20T18:05:33.081Z', deletedAt: null, }, hashPagamento: 'lmlA4yCd', loginToken: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpZCI6MiwiY3BmIjoiMDMwLjk1My44ODEtNTIiLCJyZyI6IiIsImNuaCI6IjEyMzEyMzEyMy0xMiIsImNhdGVnb3JpYUNuaCI6IiIsImRhdGFQcmltZWlyYUhhYmlsaXRhY2FvIjpudWxsLCJlc3RhZG8iOiJQRSIsIm11bmljaXBpbyI6IiIsImF6dXJlUGVyc29uSWQiOiI1M2I2ZDQ2Ny1iYzMwLTRmMmEtOTZiMS1iMmI1OTVlYjczYzciLCJkYXRhTmFzY2ltZW50byI6IjE5ODktMDUtMDVUMDM6MDA6MDAuMDAwWiIsImVtYWlsIjoidmljdG9yaXZlbnMwNUBnbWFpbC5jb20iLCJub21lIjoiVklDVE9SIElWRU5TIFBFUkVJUkEiLCJ0ZWxlZm9uZSI6Iig2MikgOTk5OTEtMTIyMiIsImF2YXRhciI6bnVsbCwiY29yIjoicmdiKDEyNiwgMjcsIDI1KSIsInVzZXJEZWxldGlvbklkIjpudWxsLCJpZ25vcmFyVmFsaWRhY2FvU2VycHJvIjp0cnVlLCJpZ25vcmFyVmFsaWRhY2FvQXp1cmUiOm51bGwsInJkTGVhZElkIjpudWxsLCJyZENvbnRhY3RJZCI6bnVsbCwiYmV0YVRlc3RlciI6bnVsbCwiY3JlYXRlZEF0IjoiMjAyMC0wOS0yOFQxODo1ODoxOS4zNTVaIiwidXBkYXRlZEF0IjoiMjAyNi0wNC0wMlQxODowNjowOC41MTFaIiwiZGVsZXRlZEF0IjpudWxsLCJtYXRyaWN1bGFJZCI6MTk1OSwiY3Vyc29JZCI6MSwic2hvdWxkQ2hhbmdlUGFzc3dvcmQiOnRydWUsImlhdCI6MTc3NTE3NTQzOCwiZXhwIjoxNzc1MjE4NjM4fQ.wJo6l5cMe_prvVoe-F5RKehu7EGhYjlD5lWn-s0uBmQ', },
  matriculaResult: null,
  cobrancaAgregada: null,
  loading: false,

  setCnhFile: (file) => set({ cnhFile: file, cnhExtraida: null }),

  // A failed read is not an error the parceiro needs to act on — the form is still there to fill by
  // hand — so it lands as an empty result the UI reports, not as a thrown exception.
  extrairDadosDaCnh: async (file) => {
    set({ cnhExtraindo: true, cnhExtraida: null })
    try {
      const { campos } = await parceirosApi.extrairCnh(file)
      set({ cnhExtraida: campos })
    } catch {
      set({ cnhExtraida: {} })
    } finally {
      set({ cnhExtraindo: false })
    }
  },

  addCursoAdicional: () => set({ cursosAdicionais: [...get().cursosAdicionais, null] }),

  setCursoAdicional: (index, cursoId) => {
    const next = [...get().cursosAdicionais]
    next[index] = cursoId
    set({ cursosAdicionais: next })
    get().sincronizarValorComPiso()
  },

  removeCursoAdicional: (index) => {
    set({ cursosAdicionais: get().cursosAdicionais.filter((_, i) => i !== index) })
    get().sincronizarValorComPiso()
  },

  clearAluno: () => set({ alunoExistente: null }),

  setUfDestino: (uf) => set({ ufDestino: uf }),

  searchAlunoByCpf: async (cpf, setValues) => {
    set({ searchingCpf: true, alunoExistente: null })
    try {
      const aluno = await alunosApi.getAlunoByCpf(cpf)
      set({ alunoExistente: aluno })
      setValues({
        cpf,
        nome: aluno.nome ?? '',
        dataNascimento: formatIsoToBrazilian(aluno.dataNascimento),
        telefone: aluno.telefone ?? '',
        email: aluno.email ?? '',
        cnh: aluno.cnh ?? '',
        categoriaCnh: aluno.categoriaCnh ?? '',
        dataPrimeiraHabilitacao: formatIsoToBrazilian(aluno.dataPrimeiraHabilitacao),
        rg: aluno.rg ?? '',
        municipio: aluno.municipio ?? '',
        uf: aluno.uf ?? '',
      } as Partial<MatriculaFormValues>)
    } catch {
      set({ alunoExistente: null })
    } finally {
      set({ searchingCpf: false })
    }
  },

  loadCursos: async (ufDestino) => {
    const [allCursos, cursosDisponiveis] = await Promise.all([
      cursosApi.getAllCursos(),
      cursosApi.getCursosByUf(ufDestino),
    ])

    const cursosTree = COURSE_CATEGORIES.map(({ label, value }) => {
      const cursosCategoria = allCursos
        .filter((c) => c.categorias === value && cursosDisponiveis.includes(c.id))
        .sort((a, b) => a.id - b.id)

      return {
        title: label,
        value: `cat-${label}`,
        selectable: false,
        children: cursosCategoria.map((c) => ({
          title: c.nomeReduzido || c.nome,
          value: c.id,
          selectable: true,
        })),
      }
    })

    // Preserve the current course selections across a UF change — only the catalog/tree is refreshed.
    // Reprice is done separately (recalcularParaUf) so all selected courses keep their slots.
    set({ allCursos, cursosTree })
  },

  // Reprice every currently selected course (primary + additionals) for a (new) UF, keeping the slots.
  recalcularParaUf: async (cnpjParceiro, ufParceiro, ufDestino) => {
    const { cursoSelecionado, cursosAdicionais } = get()
    if (cursoSelecionado != null) {
      await Promise.all([
        get().getValoresParceiros(cnpjParceiro, ufParceiro, ufDestino, cursoSelecionado),
        get().getCamposFormulario(ufDestino, cursoSelecionado),
      ])
    }
    await Promise.all(
      cursosAdicionais
        .filter((id): id is number => id != null)
        .map((id) => get().getValoresCursoAdicional(cnpjParceiro, ufParceiro, ufDestino, id))
    )
  },

  setCurso: (cursoId) => set({ cursoSelecionado: cursoId, valoresParceiros: null, campos: [] }),

  getValoresParceiros: async (cnpjParceiro, ufParceiro, ufDestino, cursoId) => {
    set({ loadingValores: true })
    try {
      const valores = await buscarValoresOuNulo({ cnpjParceiro, ufParceiro, ufDestino, cursoId })
      set({
        valoresParceiros: valores,
        valoresPorCurso: valores
          ? { ...get().valoresPorCurso, [cursoId]: valores }
          : get().valoresPorCurso,
        semPrecoPorCurso: { ...get().semPrecoPorCurso, [cursoId]: !valores },
      })
      get().sincronizarValorComPiso()
    } finally {
      set({ loadingValores: false })
    }
  },

  // Same fetch as the primary, but only records the course's values into the map (does not touch the
  // single-course billing fields). Used for additional courses so the aggregated total is correct.
  getValoresCursoAdicional: async (cnpjParceiro, ufParceiro, ufDestino, cursoId) => {
    const valores = await buscarValoresOuNulo({ cnpjParceiro, ufParceiro, ufDestino, cursoId })
    set({
      valoresPorCurso: valores
        ? { ...get().valoresPorCurso, [cursoId]: valores }
        : get().valoresPorCurso,
      semPrecoPorCurso: { ...get().semPrecoPorCurso, [cursoId]: !valores },
    })
    get().sincronizarValorComPiso()
  },

  getCamposFormulario: async (ufDestino, cursoId) => {
    const campos = await matriculasApi.getCamposFormulario(ufDestino, cursoId)
    set({ campos })
  },

  loadCaptadores: async () => {
    try {
      set({ captadores: await parceirosApi.listarCaptadores() })
    } catch {
      set({ captadores: [] })
    }
  },

  setCaptador: (captadorId) => set({ captadorId }),

  verificarContaDeRecebimento: async () => {
    const { captadorId } = get()
    try {
      set({ contaDeRecebimento: await parceirosApi.verificarContaDeRecebimento(captadorId ?? undefined) })
    } catch {
      set({ contaDeRecebimento: null })
    }
  },

  setValor: (valor) => set({ valor, valorEditadoManualmente: true }),

  // Keeps "Valor total a cobrar do aluno" honest as courses come and go: it tracks the floor for the
  // WHOLE selection until the parceiro types a price of their own, and from then on it is only ever
  // pushed UP — never back down onto a single course's cost, and never silently below the floor.
  sincronizarValorComPiso: () => {
    const piso = pisoTotal(get())
    const { valor, valorEditadoManualmente } = get()
    if (!valorEditadoManualmente || valor < piso) set({ valor: piso })
  },

  setModalidadeCobranca: (mode) => set({ modalidadeCobranca: mode }),

  validarAluno: async (data) => {
    set({ validacaoAluno: { ...emptyValidacao, loading: true } })
    try {
      const result = await matriculasApi.validarAluno({
        ...data,
        dataNascimento: parseBrazilianToIso(data.dataNascimento),
        dataPrimeiraHabilitacao: data.dataPrimeiraHabilitacao
          ? parseBrazilianToIso(data.dataPrimeiraHabilitacao)
          : undefined,
      })
      set({
        validacaoAluno: {
          loading: false,
          executou: true,
          valido: result.success,
          tipoErro: result.errorType ?? '',
          mensagemErro: result.errorMessage ?? '',
        },
      })
    } catch (erro: unknown) {
      set({
        validacaoAluno: {
          loading: false,
          executou: true,
          valido: false,
          tipoErro: 'OTHER',
          mensagemErro: getApiErrorMessage(erro, 'Erro ao validar aluno.'),
        },
      })
    }
  },

  // Validates EVERY selected course independently — a student apt for Reciclagem in GO may be barred
  // from Especialização there, so one course's verdict never speaks for another. Runs as a complete
  // pass BEFORE any matrícula is created, so a late failure can't leave earlier courses enrolled.
  validarTodosOsCursos: async (values) => {
    const { cursoSelecionado, cursosAdicionais } = get()
    const cursoIds = [cursoSelecionado, ...cursosAdicionais].filter((id): id is number => id != null)
    if (!cursoIds.length) return false

    const dataNascimento = parseBrazilianToIso(values.dataNascimento)
    const dataPrimeiraHabilitacao = values.dataPrimeiraHabilitacao
      ? parseBrazilianToIso(values.dataPrimeiraHabilitacao)
      : undefined

    set({
      validacaoPorCurso: Object.fromEntries(
        cursoIds.map((id) => [id, { ...emptyValidacao, loading: true }])
      ),
    })

    const resultados = await Promise.all(
      cursoIds.map(async (cursoId): Promise<[number, ValidacaoAluno]> => {
        try {
          const result = await matriculasApi.validarAluno({
            nome: values.nome,
            dataNascimento,
            cpf: values.cpf,
            email: values.email,
            telefone: values.telefone,
            uf: values.ufDestino,
            municipio: values.municipio,
            cursoId,
            cnh: values.cnh,
            categoriaCnh: values.categoriaCnh,
            dataPrimeiraHabilitacao,
            rg: values.rg,
            renach: values.renach,
          })
          return [
            cursoId,
            {
              loading: false,
              executou: true,
              valido: result.success,
              tipoErro: result.errorType ?? '',
              mensagemErro: result.errorMessage ?? '',
            },
          ]
        } catch (erro: unknown) {
          return [
            cursoId,
            {
              loading: false,
              executou: true,
              valido: false,
              tipoErro: 'OTHER',
              mensagemErro: getApiErrorMessage(erro, 'Erro ao validar aluno.'),
            },
          ]
        }
      })
    )

    set({ validacaoPorCurso: Object.fromEntries(resultados) })
    return resultados.every(([, validacao]) => validacao.valido)
  },

  // A única porta de entrada da validação: escolhe entre um curso e vários, e guarda a chave dos
  // dados validados. Chamada de novo sobre a mesma seleção, devolve o veredito que já está em tela —
  // é o que faz o botão "Validar" dispensar a validação do envio.
  validarSelecao: async (values) => {
    const { cursoSelecionado, cursosAdicionais } = get()
    if (!cursoSelecionado) return false

    const chave = chaveDaValidacao(get(), values)
    if (get().validacaoSnapshot === chave) return vereditoDaValidacao(get())

    if (cursosAdicionais.some((id) => id != null)) {
      const todosAptos = await get().validarTodosOsCursos(values)
      set({ validacaoAluno: emptyValidacao, validacaoSnapshot: chave })
      return todosAptos
    }

    await get().validarAluno({
      nome: values.nome,
      dataNascimento: values.dataNascimento,
      cpf: values.cpf,
      email: values.email,
      telefone: values.telefone,
      uf: values.ufDestino,
      municipio: values.municipio,
      cursoId: cursoSelecionado,
      cnh: values.cnh,
      categoriaCnh: values.categoriaCnh,
      dataPrimeiraHabilitacao: values.dataPrimeiraHabilitacao,
      rg: values.rg,
      renach: values.renach,
    })
    set({ validacaoPorCurso: {}, validacaoSnapshot: chave })
    return get().validacaoAluno.valido
  },

  limparValidacao: () =>
    set({ validacaoAluno: emptyValidacao, validacaoPorCurso: {}, validacaoSnapshot: null }),

  criarMatricula: async (cnpjParceiro, ufParceiro, values) => {
    const { valor, cursoSelecionado } = get()
    if (!cursoSelecionado) return
    set({ loading: true })
    try {
      const result = await matriculasApi.createMatricula({
        nome: values.nome,
        dataNascimento: parseBrazilianToIso(values.dataNascimento),
        cpf: values.cpf,
        email: values.email,
        telefone: values.telefone,
        cnh: values.cnh || undefined,
        categoriaCnh: values.categoriaCnh || undefined,
        dataPrimeiraHabilitacao: values.dataPrimeiraHabilitacao
          ? parseBrazilianToIso(values.dataPrimeiraHabilitacao)
          : undefined,
        cursoId: cursoSelecionado,
        valor,
        cnpjParceiro,
        uf: values.ufDestino,
        ufDestino: values.ufDestino,
        ufParceiro,
        municipio: values.municipio || undefined,
        renach: values.renach || undefined,
        rg: values.rg || undefined,
      })
      set({ matriculaResult: result })
    } finally {
      set({ loading: false })
    }
  },

  // Multi-course: create one matrícula per selected course, then group all their Pagamentos into a
  // single aggregated charge the student pays once (one pagamentoId settles the bundle).
  // Callers MUST have run validarTodosOsCursos first — nothing is created until every course passed,
  // so a course rejected by its state can never leave the earlier ones enrolled and orphaned.
  criarMatriculasComCobranca: async (cnpjParceiro, ufParceiro, values) => {
    const { cursoSelecionado, cursosAdicionais, ufDestino, valor } = get()
    const cursoIds = [cursoSelecionado, ...cursosAdicionais].filter((id): id is number => id != null)
    if (!cursoIds.length) return

    set({ loading: true })
    try {
      const dataNascimentoIso = parseBrazilianToIso(values.dataNascimento)
      const dataPrimeiraHabilitacaoIso = values.dataPrimeiraHabilitacao
        ? parseBrazilianToIso(values.dataPrimeiraHabilitacao)
        : undefined

      const matriculaIds: number[] = []
      for (const cursoId of cursoIds) {
        const valores = await matriculasApi.getValoresParceiros({
          curso: cursoId,
          cnpjParceiro,
          ufParceiro,
          ufDestino,
        })
        const result = await matriculasApi.createMatricula({
          nome: values.nome,
          dataNascimento: dataNascimentoIso,
          cpf: values.cpf,
          email: values.email,
          telefone: values.telefone,
          cnh: values.cnh || undefined,
          categoriaCnh: values.categoriaCnh || undefined,
          dataPrimeiraHabilitacao: dataPrimeiraHabilitacaoIso,
          cursoId,
          valor: valores.valorNewDriver,
          cnpjParceiro,
          uf: values.ufDestino,
          ufDestino: values.ufDestino,
          ufParceiro,
          municipio: values.municipio || undefined,
          renach: values.renach || undefined,
          rg: values.rg || undefined,
        })
        matriculaIds.push(result.id)
      }

      // The parceiro's own price for the bundle (>= the floor) rides on the umbrella charge; the
      // individual matrículas keep New Driver's cost as their valorNewDriver.
      const cobrancaAgregada = await matriculasApi.createCobrancaAgregada(matriculaIds, valor)
      set({ cobrancaAgregada })
    } finally {
      set({ loading: false })
    }
  },

  resetForm: () =>
    set({
      alunoExistente: null,
      cursoSelecionado: null,
      cursosAdicionais: [],
      cobrancaAgregada: null,
      ufDestino: '',
      valoresParceiros: null,
      valoresPorCurso: {},
      semPrecoPorCurso: {},
      captadorId: null,
      contaDeRecebimento: null,
      valor: 0,
      valorEditadoManualmente: false,
      modalidadeCobranca: 'link',
      campos: [],
      validacaoAluno: emptyValidacao,
      validacaoPorCurso: {},
      validacaoSnapshot: null,
      matriculaResult: null,
      cnhFile: null,
      cnhExtraida: null,
      cnhExtraindo: false,
    }),
}))
